const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const TOKEN_KEY = 'policyora_token';

function authHeaders(json = true): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {};
  if (json) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let detail = `Request failed (${res.status})`;
    try { detail = JSON.parse(body).detail ?? detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

// --- Types ---

export interface RiskStatus {
  session_id: string;
  status: string;
  current_node: string | null;
}

export interface RiskQuestion {
  question_id: string;
  element_id?: string;
  dimension_id?: string;
  policy_id: string;
  question_text: string;
  answer_text?: string | null;
  question_type?: 'document_request' | 'standalone_question' | null;
  document_type?: string | null;
  document_description?: string | null;
  resolves_question_ids?: string[] | null;
  resolves_dimension_ids?: string[] | null;
}

export interface RiskAreaScore {
  risk_score: number;
  risk_rating: string;
  avg_likelihood: string;
  avg_impact: string;
  avg_control_effectiveness: number;
  dimensions_mitigated: number;
  dimensions_partially_mitigated: number;
  dimensions_unmitigated: number;
  top_residual_risks: string[];
}

export interface RiskDimensionResult {
  dimension_id: string;
  status: string;
  evidence_used: string[];
  residual_risk: string | null;
  likelihood: string;
  impact: string;
  control_effectiveness: number;
  risk_score: number;
  severity: string | null;
  recommendation: string | null;
  question?: string;
  area?: string;
}

export interface PolicyCoverage {
  raw_dimensions: number;
  covered: number;
  mitigated: number;
  partial: number;
  unmitigated: number;
}

export interface DeferredSummary {
  total: number;
  by_area: Record<string, number>;
}

export interface MergeStats {
  raw_count: number;
  post_dedup_count: number;
  post_filter_count?: number;
  deferred_count?: number;
  per_area_counts: Record<string, number>;
  deferred_by_area?: Record<string, number>;
}

export interface ResolutionStats {
  total_raw_questions: number;
  resolved_by_existing_docs: number;
  resolved_by_uploaded_docs: number;
  resolved_by_text_answers: number;
  document_requests_sent: number;
  standalone_questions_sent: number;
}

export interface RiskResult {
  company_id: string;
  session_id: string;
  engine: string;
  overall_risk_score: number;
  health_rating: string;
  area_risk_scores: Record<string, RiskAreaScore>;
  dimension_results: RiskDimensionResult[];
  risk_heatmap: Record<string, Record<string, number>>;
  critical_risks: (RiskDimensionResult & { question: string; area: string })[];
  top_recommendations: { recommendation: string; severity: string | null; risk_score: number; dimension_id: string; question: string }[];
  evidence_map: Record<string, { question: string; evidence_used: string[]; doc_sources: string[] }>;
  assessed_at: string;
  hitl_iterations: number;
  total_elements: number;
  elements_met: number;
  elements_partial: number;
  elements_missing: number;
  coverage_score: number;
  policy_coverage?: Record<string, PolicyCoverage>;
  deferred_summary?: DeferredSummary;
  merge_stats?: MergeStats;
  resolution_stats?: ResolutionStats;
}

// --- Dashboard types ---

export interface RiskAssessmentSummary {
  assessment_id: string | null;
  session_id: string;
  version: number | null;
  jurisdiction: string | null;
  overall_score: number | null;
  health_rating: string | null;
  status: string;
  current_node: string | null;
  total_regulations: number | null;
  total_dimensions?: number | null;
  dimensions_met?: number | null;
  dimensions_partial?: number | null;
  dimensions_missing?: number | null;
  coverage_score?: number | null;
  hitl_iterations?: number | null;
  assessed_at: string | null;
  created_at: string | null;
}

export interface RiskAssessmentDetail {
  assessment_id: string;
  version: number;
  session_id: string;
  jurisdiction: string | null;
  overall_score: number | null;
  health_rating: string | null;
  status: string;
  assessed_at: string | null;
  final_result: RiskResult | null;
  questions: RiskLatestResultQuestion[];
  dimension_details: Record<string, RiskDimensionDetail>;
}

export interface RiskAssessmentAreaScore {
  area: string;
  area_label: string | null;
  score: number;
  coverage: number;
  dimensions_met: number;
  dimensions_partial: number;
  dimensions_missing: number;
}

export interface RiskAssessmentGap {
  dimension_id: string;
  area: string;
  question: string;
  status: string;
  gap: string | null;
  severity: string | null;
  score_contribution: number;
  recommendation: string | null;
  evidence_used: string[] | null;
  source_artifact_ids: string[] | null;
}

export interface AnswerFile {
  id: number;
  file_s3_key: string;
  file_name: string | null;
  file_size_bytes: number | null;
  content_type: string | null;
}

export interface AnswerVersion {
  version_id: number;
  version: number;
  answer_text: string | null;
  created_at: string | null;
  files: AnswerFile[];
}

export interface RiskLatestResultQuestion {
  id: number;
  question_id: string;
  dimension_id: string | null;
  policy_id: string | null;
  question_text: string;
  question_type: string | null;
  latest_answer: AnswerVersion | null;
  answer_versions: AnswerVersion[];
}

export interface RiskDimensionDetail {
  dimension_id: string;
  area: string;
  question: string;
  status: string;
  score_contribution: number | null;
  severity: string | null;
  policy_reference: string | null;
  company_evidence_summary: string | null;
  reasoning: string | null;
  gap: string | null;
  recommendation: string | null;
  evidence_used: string[] | null;
  doc_sources: string[] | null;
  source_policy_id: string | null;
  source_policy_name: string | null;
}

export interface RiskLatestResult {
  assessment_id: string;
  version: number;
  jurisdiction: string | null;
  assessed_at: string | null;
  final_result: RiskResult;
  questions: RiskLatestResultQuestion[];
  dimension_details: Record<string, RiskDimensionDetail>;
}

// --- API endpoints ---

export async function getRiskStatus(sessionId: string): Promise<RiskStatus> {
  const res = await fetch(`${API_BASE_URL}/api/v1/risk/status/${sessionId}`, {
    headers: authHeaders(),
  });
  return parseResponse<RiskStatus>(res);
}

export async function getRiskQuestions(sessionId: string): Promise<RiskQuestion[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/risk/questions/${sessionId}`, {
    headers: authHeaders(),
  });
  return parseResponse<RiskQuestion[]>(res);
}

export async function submitRiskAnswers(
  sessionId: string,
  answers: { question_id: string; answer_text: string }[]
): Promise<{ status: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/risk/answers/${sessionId}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ answers }),
  });
  return parseResponse<{ status: string }>(res);
}

export async function getRiskResults(sessionId: string): Promise<RiskResult | { status: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/risk/results/${sessionId}`, {
    headers: authHeaders(),
  });
  return parseResponse<RiskResult | { status: string }>(res);
}

export async function uploadRiskDocument(
  sessionId: string,
  questionId: string,
  file: File,
): Promise<{ status: string; question_id: string; s3_key: string; filename: string }> {
  const form = new FormData();
  form.append('question_id', questionId);
  form.append('file', file);

  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}/api/v1/risk/upload-document/${sessionId}`, {
    method: 'POST',
    headers,
    body: form,
  });
  return parseResponse(res);
}

// --- Dashboard API ---

export async function listRiskAssessments(): Promise<RiskAssessmentSummary[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/risk/assessments`, {
    headers: authHeaders(),
  });
  return parseResponse<RiskAssessmentSummary[]>(res);
}

export async function getRiskAssessmentDetail(assessmentId: string): Promise<RiskAssessmentDetail> {
  const res = await fetch(`${API_BASE_URL}/api/v1/risk/assessments/${assessmentId}`, {
    headers: authHeaders(),
  });
  return parseResponse<RiskAssessmentDetail>(res);
}

export async function getRiskLatestResult(): Promise<RiskLatestResult> {
  const res = await fetch(`${API_BASE_URL}/api/v1/risk/latest-result`, {
    headers: authHeaders(),
  });
  return parseResponse<RiskLatestResult>(res);
}

export async function saveRiskAnswerVersion(
  questionRowId: number,
  answerText: string | null,
): Promise<AnswerVersion> {
  const res = await fetch(`${API_BASE_URL}/api/v1/risk/questions/${questionRowId}/answer`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ answer_text: answerText }),
  });
  return parseResponse<AnswerVersion>(res);
}

export async function uploadRiskAnswerFile(
  questionRowId: number,
  versionId: number,
  file: File,
): Promise<AnswerFile> {
  const form = new FormData();
  form.append('file', file);

  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(
    `${API_BASE_URL}/api/v1/risk/questions/${questionRowId}/answer/${versionId}/upload`,
    { method: 'POST', headers, body: form },
  );
  return parseResponse<AnswerFile>(res);
}
